import React, { useEffect, useState } from "react";
import JournalCalendar from "../calendar/JournalCalendar";
import EntryDetail from "../journal/EntryDetail";
import GoalDetail from "../goals/GoalDetail";
import EntryForm from "../journal/EntryForm";
import GoalForm from "../goals/GoalForm";
import Modal from "../common/Modal";
import ConfirmDialog from "../common/ConfirmDialog";

import {
  fetchCalendarData,
  addCalendarUpdateListener,
  removeCalendarUpdateListener,
  createJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  createGoal,
  updateGoal,
  deleteGoal,
} from "../../api/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "../../i18n/LanguageContext";

const CalendarPage = () => {
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["calendarData"],
    queryFn: fetchCalendarData,
    staleTime: 5 * 60 * 1000,
  });

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showEntryFormModal, setShowEntryFormModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [initialEntryDate, setInitialEntryDate] = useState(null);
  const [showGoalFormModal, setShowGoalFormModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAllEntries, setShowAllEntries] = useState(false);
  const [showAllGoals, setShowAllGoals] = useState(false);

  const PANEL_LIMIT = 3;

  const journalEntries = data?.journalEntries || [];
  const goals = data?.goals || [];

  useEffect(() => {
    const unsubscribe = addCalendarUpdateListener(() => {
      queryClient.invalidateQueries(["calendarData"]);
    });

    return () => {
      removeCalendarUpdateListener(unsubscribe);
    };
  }, [queryClient]);

  const createEntryMutation = useMutation({
    mutationFn: createJournalEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarData"] });
      closeEntryFormModal();
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: ({ entryId, entryData }) =>
      updateJournalEntry(entryId, entryData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarData"] });
      closeEntryFormModal();
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: deleteJournalEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarData"] });
      setSelectedEntry(null); // Close detail modal if open
      setPendingDelete(null);
    },
  });

  const createGoalMutation = useMutation({
    mutationFn: createGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarData"] });
      closeGoalFormModal();
    },
  });

  const updateGoalMutation = useMutation({
    mutationFn: ({ goalId, goalData }) => updateGoal(goalId, goalData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarData"] });
      closeGoalFormModal();
    },
  });

  const deleteGoalMutation = useMutation({
    mutationFn: deleteGoal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendarData"] });
      setSelectedGoal(null); // Close detail modal if open
      setPendingDelete(null);
    },
  });

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
  };

  const handleGoalClick = (goal) => {
    setSelectedGoal(goal);
  };

  const handleDayClick = (dayInfo) => {
    setSelectedDay(dayInfo);
    setShowAllEntries(false);
    setShowAllGoals(false);
  };

  const handleCloseDetail = () => {
    setSelectedEntry(null);
    setSelectedGoal(null);
  };

  const openEditEntryModal = (entry) => {
    setSelectedEntry(null); // Close detail modal
    setEditingEntry(entry);
    setShowEntryFormModal(true);
  };

  const closeEntryFormModal = () => {
    setShowEntryFormModal(false);
    setEditingEntry(null);
    setInitialEntryDate(null);
  };

  const openCreateEntryForSelectedDay = () => {
    if (!selectedDay?.fullDate) return;
    const date = selectedDay.fullDate.toISOString().split("T")[0];
    setEditingEntry(null);
    setInitialEntryDate(date);
    setShowEntryFormModal(true);
  };

  const handleSaveEntry = async (entryToSave) => {
    const entryData = {
      content: entryToSave.content,
      sentiment_level: entryToSave.sentiment,
      sleep_quality: entryToSave.sleep,
      stress_level: entryToSave.stress,
      social_engagement: entryToSave.socialEngagement,
      date: entryToSave.date,
      title: entryToSave.title,
    };

    if (entryToSave.id) {
      updateEntryMutation.mutate({ entryId: entryToSave.id, entryData });
    } else {
      createEntryMutation.mutate(entryData);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    setPendingDelete({ type: "entry", id: entryId });
  };

  const confirmDelete = () => {
    if (!pendingDelete) {
      return;
    }
    if (pendingDelete.type === "entry") {
      deleteEntryMutation.mutate(pendingDelete.id);
    } else {
      deleteGoalMutation.mutate(pendingDelete.id);
    }
  };

  const openEditGoalModal = (goal) => {
    setSelectedGoal(null); // Close detail modal
    setEditingGoal(goal);
    setShowGoalFormModal(true);
  };

  const closeGoalFormModal = () => {
    setShowGoalFormModal(false);
    setEditingGoal(null);
  };

  const handleSaveGoal = async (goalToSave) => {
    const goalData = {
      title: goalToSave.title,
      type: goalToSave.type,
      targetDate: goalToSave.targetDate,
      category: goalToSave.category,
      priority: goalToSave.priority,
      description: goalToSave.description,
      ...(goalToSave.id && { progress: goalToSave.progress }),
    };

    if (goalToSave.id) {
      updateGoalMutation.mutate({ goalId: goalToSave.id, goalData });
    } else {
      createGoalMutation.mutate(goalData);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    setPendingDelete({ type: "goal", id: goalId });
  };

  return (
    <div className="calendar-page-container p-4">
      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <p className="text-center text-gray-500 italic">
            {t("calendar.loading")}
          </p>
        </div>
      )}

      {isError && (
        <div className="flex justify-center items-center h-64">
          <p className="text-center text-red-500 italic">
            {t("common.error")}: {error?.message || t("calendar.failed")}
          </p>
        </div>
      )}

      {!isLoading && !isError && data && (
        <JournalCalendar
          journalEntries={journalEntries}
          goals={goals}
          onEntryClick={handleEntryClick}
          onGoalClick={handleGoalClick}
          onDayClick={handleDayClick}
        />
      )}

      {selectedDay && !selectedEntry && !selectedGoal && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/20 backdrop-blur-sm" onClick={() => setSelectedDay(null)}>
          <aside
            className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">{t("calendar.dayDetail")}</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-900">
                  {selectedDay.fullDate.toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setSelectedDay(null)}
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label={t("calendar.closeDayDetail")}
              >
                x
              </button>
            </div>

            <button
              type="button"
              onClick={openCreateEntryForSelectedDay}
              className="mt-5 w-full rounded-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/30"
            >
              {t("calendar.newEntryForDay")}
            </button>

            <section className="mt-6 space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t("calendar.entries")}</h3>
              {selectedDay.entries.length > 0 ? (
                <>
                  {(showAllEntries ? selectedDay.entries : selectedDay.entries.slice(0, PANEL_LIMIT)).map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => handleEntryClick(entry)}
                      className="block w-full rounded-2xl border border-gray-100 bg-gray-50 p-4 text-left hover:bg-purple-50"
                    >
                      <p className="font-semibold text-gray-900">{entry.title}</p>
                      <p className="mt-1 line-clamp-2 text-sm text-gray-600">{entry.content}</p>
                    </button>
                  ))}
                  {selectedDay.entries.length > PANEL_LIMIT && (
                    <button
                      type="button"
                      onClick={() => setShowAllEntries((prev) => !prev)}
                      className="text-sm font-medium text-purple-600 hover:underline"
                    >
                      {showAllEntries
                        ? t("calendar.showLess")
                        : t("calendar.showAllEntries", { count: selectedDay.entries.length })}
                    </button>
                  )}
                </>
              ) : (
                <p className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-500">{t("calendar.noEntries")}</p>
              )}
            </section>

            <section className="mt-6 space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t("calendar.goals")}</h3>
              {selectedDay.goals.length > 0 ? (
                <>
                  {(showAllGoals ? selectedDay.goals : selectedDay.goals.slice(0, PANEL_LIMIT)).map((goal) => (
                    <button
                      key={goal.id}
                      type="button"
                      onClick={() => handleGoalClick(goal)}
                      className="block w-full rounded-2xl border border-gray-100 bg-gray-50 p-4 text-left hover:bg-emerald-50"
                    >
                      <p className="font-semibold text-gray-900">{goal.title}</p>
                      <p className="mt-1 text-sm text-gray-600">{goal.progress || 0}% {t("goal.complete")}</p>
                    </button>
                  ))}
                  {selectedDay.goals.length > PANEL_LIMIT && (
                    <button
                      type="button"
                      onClick={() => setShowAllGoals((prev) => !prev)}
                      className="text-sm font-medium text-purple-600 hover:underline"
                    >
                      {showAllGoals
                        ? t("calendar.showLess")
                        : t("calendar.showAllGoals", { count: selectedDay.goals.length })}
                    </button>
                  )}
                </>
              ) : (
                <p className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-500">{t("calendar.noGoals")}</p>
              )}
            </section>
          </aside>
        </div>
      )}

      {selectedEntry && (
        <EntryDetail
          entry={selectedEntry}
          onClose={handleCloseDetail}
          onEdit={openEditEntryModal}
          onDelete={handleDeleteEntry}
        />
      )}

      {selectedGoal && (
        <GoalDetail
          goal={selectedGoal}
          onClose={handleCloseDetail}
          onEdit={openEditGoalModal}
          onDelete={handleDeleteGoal}
        />
      )}

      {showEntryFormModal && (
        <Modal
          title={editingEntry ? t("journal.editEntry") : t("journal.newEntry")}
          onClose={closeEntryFormModal}
        >
          <EntryForm
            onClose={closeEntryFormModal}
            onSave={handleSaveEntry}
            editEntry={editingEntry}
            initialDate={initialEntryDate}
          />
        </Modal>
      )}

      {showGoalFormModal && (
        <Modal
          title={editingGoal ? t("goals.edit") : t("goals.create")}
          onClose={closeGoalFormModal}
        >
          <GoalForm
            onClose={closeGoalFormModal}
            onSave={handleSaveGoal}
            editGoal={editingGoal}
          />
        </Modal>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={pendingDelete.type === "entry" ? t("journal.deleteTitle") : t("goals.deleteTitle")}
          message={
            pendingDelete.type === "entry"
              ? t("journal.deleteMessage")
              : t("goals.deleteMessage")
          }
          confirmLabel={pendingDelete.type === "entry" ? t("journal.deleteConfirm") : t("goals.deleteConfirm")}
          isConfirming={deleteEntryMutation.isPending || deleteGoalMutation.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default CalendarPage;
